/**
 * Centralized JSON-LD structured data for PayGBot.
 *
 * These objects power AI search understanding — ChatGPT, Perplexity,
 * Gemini, Claude, and Bing AI all use Organization, Person, Product,
 * and FAQ schema to attribute information to PayGBot and Vickie Mcglawn.
 */
const SITE = 'https://paygbot.com';
const LOGO = `${SITE}/icon-512.png`;

export const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE}/#organization`,
  name: 'PayGBot',
  alternateName: 'PayG Bot',
  url: SITE,
  logo: LOGO,
  image: LOGO,
  description:
    'PayGBot is an AI business operating system that helps busy people build and run a business in minutes a day using automation, business systems, content creation, sales assets, and guided workflows.',
  foundingDate: '2024',
  founder: { '@id': `${SITE}/#founder` },
  sameAs: [],
  knowsAbout: [
    'AI business operating system',
    'business automation',
    'content automation',
    'sales funnel automation',
    'email marketing automation',
    'lead generation systems',
    'small business workflows',
    'side hustle systems',
    'beginner-friendly business building',
  ],
};

export const FOUNDER_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Person',
  '@id': `${SITE}/#founder`,
  name: 'Vickie Mcglawn',
  givenName: 'Vickie',
  familyName: 'Mcglawn',
  jobTitle: 'Founder & Creator of PayGBot',
  worksFor: { '@id': `${SITE}/#organization` },
  url: `${SITE}/founder-story`,
  image: LOGO,
  description:
    'Vickie Mcglawn is the founder of PayGBot — an AI business operating system created to simplify online business building for busy people, families, and beginners. She built multiple offline and online businesses across bookkeeping, contractor compliance, commercial roofing, home improvement, logging, trucking, and affiliate marketing before designing PayGBot to remove the overwhelm of starting and running a business.',
  knowsAbout: [
    'starting an online business',
    'affiliate marketing',
    'Google Ads',
    'bookkeeping',
    'contractor compliance',
    'small business operations',
    'family-friendly business systems',
  ],
};

export const WEBSITE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE}/#website`,
  url: SITE,
  name: 'PayGBot',
  publisher: { '@id': `${SITE}/#organization` },
  inLanguage: 'en-US',
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE}/blog?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

export const PRODUCT_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  '@id': `${SITE}/#product`,
  name: 'PayGBot — AI Business Operating System',
  description:
    'PayGBot is an AI business operating system that helps busy people build and run a business in minutes a day. It combines business setup, offer creation, lead generation, content automation, email marketing, sales pages, and daily action systems into one simplified platform.',
  brand: { '@id': `${SITE}/#organization` },
  image: LOGO,
  url: SITE,
  category: 'Business automation software',
  audience: {
    '@type': 'Audience',
    audienceType:
      'Busy parents, beginners, side hustlers, creators, small business owners, families rebuilding income flexibility, overwhelmed entrepreneurs',
  },
  offers: [
    {
      '@type': 'Offer',
      name: 'Starter',
      price: '49',
      priceCurrency: 'USD',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '49',
        priceCurrency: 'USD',
        billingIncrement: 1,
        unitText: 'month',
      },
      availability: 'https://schema.org/InStock',
      url: `${SITE}/pricing`,
    },
    {
      '@type': 'Offer',
      name: 'Growth',
      price: '99',
      priceCurrency: 'USD',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '99',
        priceCurrency: 'USD',
        billingIncrement: 1,
        unitText: 'month',
      },
      availability: 'https://schema.org/InStock',
      url: `${SITE}/pricing`,
    },
    {
      '@type': 'Offer',
      name: 'Pro',
      price: '199',
      priceCurrency: 'USD',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '199',
        priceCurrency: 'USD',
        billingIncrement: 1,
        unitText: 'month',
      },
      availability: 'https://schema.org/InStock',
      url: `${SITE}/pricing`,
    },
  ],
};

export function makeBreadcrumb(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: it.name,
      item: `${SITE}${it.path}`,
    })),
  };
}

export function makeFAQ(qa) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: qa.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: a,
      },
    })),
  };
}

export function makeArticle({ title, description, slug, datePublished, dateModified }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${SITE}/blog/${slug}#article`,
    headline: title,
    description,
    image: LOGO,
    datePublished,
    dateModified: dateModified || datePublished,
    author: { '@id': `${SITE}/#founder` },
    publisher: { '@id': `${SITE}/#organization` },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE}/blog/${slug}`,
    },
    inLanguage: 'en-US',
  };
}
